import React from 'react';
import type { PeriodComparisonItem } from '@troia-v3/types';

import { DashCard, ViewToggle, formatBRL, formatNumber } from '@/shared/components/ui';

import type { CommercialComparisonMode } from '../hooks/useCommercialDashboardPreferences';

interface ComparisonCardProps {
  comparison: PeriodComparisonItem[];
  mode: CommercialComparisonMode;
  onModeChange: (mode: CommercialComparisonMode) => void;
}

const MODES = [
  { id: 'revenue' as const, icon: 'dollar-sign' as const, label: 'Receita', tooltip: 'Receita entre períodos' },
  { id: 'deals' as const, icon: 'shopping-bag' as const, label: 'Vendas', tooltip: 'Número de vendas entre períodos' },
];

export const ComparisonCard: React.FC<ComparisonCardProps> = ({ comparison, mode, onModeChange }) => {
  // `?? 0`: resposta anterior ao campo `deals` não pode desenhar "NaN".
  const valueOf = (item: PeriodComparisonItem): number =>
    mode === 'revenue' ? item.revenue : (item.deals ?? 0);
  const format = mode === 'revenue' ? formatBRL : formatNumber;
  const maxValue = comparison.reduce((max, item) => Math.max(max, valueOf(item)), 0);

  return (
    <DashCard
      icon="bar-chart"
      title="Comparativo"
      subtitle={mode === 'revenue' ? 'Receita entre períodos' : 'Vendas entre períodos'}
      headerRight={
        <ViewToggle
          modes={MODES}
          value={mode}
          onChange={onModeChange}
          // Só ícone: o card é estreito (1/4 da linha) e o subtítulo já diz o modo.
          collapsibleLabels
          data-testid="comparison-mode"
        />
      }
      testId="comparison-card"
      className="h-full"
      bodyClassName="flex flex-col justify-center gap-3"
    >
      {comparison.length === 0 && (
        <div className="py-4 text-center text-xs" style={{ color: 'var(--theme-muted-text)' }}>
          Sem dados de comparação.
        </div>
      )}
      {comparison.map((item, idx) => {
        const value = valueOf(item);
        const width = maxValue > 0 ? Math.max(2, (value / maxValue) * 100) : 0;
        return (
          <div
            key={item.label}
            className="flex flex-col gap-1.5"
            data-testid={item.isCurrent ? 'comparison-current' : `comparison-${idx}`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span
                className="text-xs truncate"
                style={{
                  color: item.isCurrent ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                  fontWeight: item.isCurrent ? 600 : 400,
                }}
              >
                {item.label}
              </span>
              <span
                className="text-sm flex-shrink-0"
                style={{
                  color: item.isCurrent ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                  fontWeight: item.isCurrent ? 700 : 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {format(value)}
              </span>
            </div>
            <div
              className="h-1.5 rounded-full overflow-hidden"
              style={{ backgroundColor: 'var(--theme-card-border)' }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${width}%`,
                  backgroundColor: item.isCurrent
                    ? 'var(--theme-primary, #8b5cf6)'
                    : 'var(--theme-muted-text)',
                }}
              />
            </div>
          </div>
        );
      })}
    </DashCard>
  );
};

ComparisonCard.displayName = 'ComparisonCard';
